import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import eventCreationModel from '../../../assets/eventCreationModel.json'
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { EventsService } from './events.service';
import { CacheResolverService } from '../../core/cache/cache-resolver.service';

@Injectable({
  providedIn: 'root'
})
export class PropertiesFormattingService {

  generalInformation = {};
  notes = {};
  productReturn = {};
  userPermissionData = [];
  
  constructor(private eventService: EventsService, 
              private dialog: MatDialog,
              private readonly cacheService: CacheResolverService) { }

  public getUserPermissions() {
    const storageKey = 'user-permissions';
    const userPermissions = this.cacheService.get(storageKey);
    if (userPermissions) {
      return Promise.resolve(userPermissions);
    }

    return new Promise((resolve, reject) => {
      let rolePermissionMapping = new Map<string, string>();
      let tempMapping: { [key: string]: string } = {};
      this.userPermissionData = [];
      this.eventService.getPermissions().toPromise().then(response => {
        this.setUserRolePermission(response) ;
        response.forEach(res => {
          rolePermissionMapping.set(res['scope'], res['permission'])
        })
        rolePermissionMapping.forEach((val, key) => {
          tempMapping[key] = val;
        });

        resolve(this.cacheService.setAndGet(storageKey, tempMapping));
      }, err => {
        reject(err);
      })
    })
  }

  eventDraftModel(model) {
    eventCreationModel['eventCreationModel']['generalInformation'] = model['generalInformation'];
    eventCreationModel['eventCreationModel']['eventProductMapping'] = model['eventProductMapping'];
    eventCreationModel['eventCreationModel']['eventLotMapping'] = model['eventLotMapping'];
    eventCreationModel['eventCreationModel']['affectedDC'] = model['affectedDC'];
    eventCreationModel['eventCreationModel']['notes'] = model['notes'];
    eventCreationModel['eventCreationModel']['productReturn'] = model['productReturn'];
    eventCreationModel['eventCreationModel']['documents'] = model['documents'];
    eventCreationModel['eventCreationModel']['qraApprover'] = model['qraApprover'];

    return eventCreationModel;
  }

  generalInfo(res) {
    this.generalInformation['eventNumber'] = res['eventNumber'];
    this.generalInformation['eventRequestor'] = res['eventRequestor'] ? res['eventRequestor']['eventRequestor'] : null;
    this.generalInformation['eventType'] = res['eventType'] ? res['eventType']['eventType'] : null;
    this.generalInformation['classificationOfEvent'] = res['classificationOfEvent'] ? res['classificationOfEvent']['eventClass'] : null;
    this.generalInformation['eventLevel'] = res['eventLevel'] ? res['eventLevel']['eventLevel'] : null;
    this.generalInformation['communicationLevel'] = res['communicationLevel'] ? res['communicationLevel']['communicationLevel'] : null;
    this.generalInformation['vendorName'] = res['vendorName'];
    this.generalInformation['eventOwner'] = res['eventOwner'];
    this.generalInformation['eventPriority'] = res['eventPriority'] ? res['eventPriority']['eventPriority'] : null;
    this.generalInformation['dateOfVendorNotice'] = res['dateOfVendorNotice'];
    this.generalInformation['dateOfNoticeReceipt'] = res['dateOfNoticeReceipt'];
    this.generalInformation['preSource'] = res['preSource'];
    this.generalInformation['formOfNotice'] = res['formOfNotice'] ? res['formOfNotice']['formOfNotice'] : null;
    this.generalInformation['firstShipDate'] = res['firstShipDate'];
    this.generalInformation['externalReferenceNo'] = res['externalReferenceNo'];
    this.generalInformation['inventorySystem'] = res['inventorySystem'] ? res['inventorySystem']['inventorySystem'] : null;
    this.generalInformation['mfgGroupCode'] = res['mfgGroupCode'] ? res['mfgGroupCode']['mfgGroupCode'] : null;
    this.generalInformation['riskAssessment'] = res['riskAssessment'] ? res['riskAssessment']['riskAssessment'] : null;
    this.generalInformation['reasonForEvent'] = res['reasonForEvent'];
    this.generalInformation['lineOfBusiness'] = 'medical';
    this.generalInformation['isPrivateLabel'] = res['isPrivateLabel'];
    this.generalInformation['dpfaFlag'] = res['dpfaFlag'];

    return this.generalInformation;
  }

  productInfo(res) {
    var selectedProducts = [];
    for (let r of res['productDisplayModelList']) {
      let array = [];
      let itemList = [];
      for (let y of r['productInformation']) {
        array.push({
          items: {
            productNumber: y['productInformation']['productNumberMedSku'],
            productIdentification: y['productInformation']['productIdentification']['productIdentification'],
            lineOfBusiness: y['productInformation']['businessSegment']['lineOfBusiness'],
            description: y['productInformation']['productDescription'],
            supplierName: y['productInformation']['vendorName'],
            cardinalHealthCatalogNumber: y['productInformation']['cardinalHealthCatalogNumber'],
            sourceSystem: y['productInformation']['sourceSystem'],
            id: y['productInformation']['productInfoId'],
            businessUnit: y['productInformation']['businessUnit'],
            businessSegment: y['productInformation']['businessSegment']['lineOfBusiness'],
            lotType: y['lotType'] || (y['lots'].toString().toLowerCase() === 'all lots' ? 'All' : 'Specific'),
            lots: y['lots'].toString().replaceAll(",", " "),
            dscsaFl: y['productInformation']['dscsaFl']
          },
          lots: y['lots']
        });
      }
      for (let arr of array) {
        itemList.push(arr['items']);
      }
      var products = {
        items: itemList,
        productIdentification: r['productIdentification']['productIdentification'],
        productIdentificationNumber: r['productIdentificationNumber'],
        lots: array[0]['lots'].toString().replaceAll(",", " ")
      }
      selectedProducts.push(products);
    }
    return selectedProducts;
  }

  notesInfo(res) {
    this.notes['internalHealthNotes'] = res['internalNotes'];
    this.notes['salesCusNotes'] = res['salesCusNotes'];
    this.notes['qraNotes'] = res['qraNotes'];

    return this.notes;
  }

  productReturnInfo(res) {
    this.productReturn['customerDisposition'] = res['customerDisposition'];
    this.productReturn['customerDesignatedName'] = res['customerDesignatedName'];
    this.productReturn['customerDesignatedContact'] = res['customerDesignatedContactNumber'];
    this.productReturn['customerDesignatedEmail'] = res['customerDesignatedEmail'];
    this.productReturn['customerDispositionComments'] = res['customerDispositionComments'];
    this.productReturn['dcDispositionType'] = res['dcDispositionType'] ? res['dcDispositionType']['dispositionType'] : null;
    this.productReturn['dcDesignatedName'] = res['dcDesignatedName'];
    this.productReturn['dcDesignatedContactNumber'] = res['dcDesignatedContactNumber'];
    this.productReturn['dcDesignatedEmail'] = res['dcDesignatedEmail'];
    this.productReturn['dcDispositionInstruction'] = res['dcDispositionInstruction'];
    this.productReturn['dcDispositionComments'] = res['dcDispositionComments'];

    return this.productReturn;
  }

  modifiedDraft(res) {
    return {
      eventNumber: res[0]['eventNumber'],
      eventType: res[0]['eventType'],
      eventLevel: res[0]['eventLevel'],
      classificationOfEvent: res[0]['classificationOfEvent'],
      communicationLevel: res[0]['communicationLevel'],
      eventPriority: res[0]['eventPriority'],
      formOfNotice: res[0]['formOfNotice'],
      firstShipDate: res[0]['firstShipDate'],
      externalReferenceNo: res[0]['externalReferenceNo'],
      mfgGroupCode: res[0]['mfgGroupCode'],
      reasonForEvent: res[0]['reasonForEvent'],
      isPrivateLabel: res[0]['isPrivateLabel'],
      affectedDc: res[0]['affectedDc'],
      dpfaFlag: res[0]['dpfaFlag'],
      customerDisposition: res[0]['customerDisposition'],
      dcDispositionInstruction: res[0]['dcDispositionInstruction'],

      dcDispositionComments: res[0]['dcDispositionComments'],
      dcDispositionType: res[0]['dcDispositionType'],
      dcDesignatedName: res[0]['dcDesignatedName'],
      dcDesignatedContactNumber: res[0]['dcDesignatedContactNumber'],
      dcDesignatedEmail: res[0]['dcDesignatedEmail'],

      customerDesignatedName: res[0]['customerDesignatedName'],
      customerDesignatedContact: res[0]['customerDesignatedContact'],
      customerDesignatedEmail: res[0]['customerDesignatedEmail'],
      customerDispositionComments: res[0]['customerDispositionComments'],
      
      eventProductMapping: res[0]['eventProductMapping'],
      eventLotMapping: res[0]['eventLotMapping'],

      bypassApprovalProcess: res[0]['bypassApprovalProcess'],

      internalHealthNotes: res[0]['internalHealthNotes'],
      salesCusNotes: res[0]['salesCusNotes'],
      qraNotes: res[0]['qraNotes']
    }
  }

  eventEditModel(eventCreationModel): any {
    return {
      eventNumber: eventCreationModel['generalInformation']['eventNumber'],
      eventType: eventCreationModel['generalInformation']['eventType'],
      eventLevel: eventCreationModel['generalInformation']['eventLevel'],
      classificationOfEvent: eventCreationModel['generalInformation']['classificationOfEvent'],
      communicationLevel: eventCreationModel['generalInformation']['communicationLevel'],
      eventPriority: eventCreationModel['generalInformation']['eventPriority'],
      formOfNotice: eventCreationModel['generalInformation']['formOfNotice'],
      firstShipDate: eventCreationModel['generalInformation']['firstShipDate'],
      externalReferenceNo: eventCreationModel['generalInformation']['externalReferenceNo'],
      mfgGroupCode: eventCreationModel['generalInformation']['mfgGroupCode'],
      reasonForEvent: eventCreationModel['generalInformation']['reasonForEvent'],
      isPrivateLabel: eventCreationModel['generalInformation']['isPrivateLabel'],
      affectedDc: !!eventCreationModel['affectedDC'] ? eventCreationModel['affectedDC'].map(d => d.dcId).join(',') : eventCreationModel['affectedDC'],
      dpfaFlag: eventCreationModel['generalInformation']['dpfaFlag'],
      customerDisposition: eventCreationModel['productReturn']['customerDisposition'],
      dcDispositionInstruction: eventCreationModel['productReturn']['dcDispositionInstruction'],

      dcDispositionComments: eventCreationModel['productReturn']['dcDispositionComments'],
      dcDispositionType: eventCreationModel['productReturn']['dcDispositionType'],
      dcDesignatedName: eventCreationModel['productReturn']['dcDesignatedName'],
      dcDesignatedContactNumber: eventCreationModel['productReturn']['dcDesignatedContactNumber'],
      dcDesignatedEmail: eventCreationModel['productReturn']['dcDesignatedEmail'],

      customerDesignatedName: eventCreationModel['productReturn']['customerDesignatedName'],
      customerDesignatedContact: eventCreationModel['productReturn']['customerDesignatedContact'],
      customerDesignatedEmail: eventCreationModel['productReturn']['customerDesignatedEmail'],
      customerDispositionComments: eventCreationModel['productReturn']['customerDispositionComments'],
      
      eventProductMapping: eventCreationModel['eventProductMapping'],
      eventLotMapping: eventCreationModel['eventLotMapping'],

      internalHealthNotes: eventCreationModel['notes']['internalHealthNotes'],
      salesCusNotes: eventCreationModel['notes']['salesCusNotes'],
      qraNotes: eventCreationModel['notes']['qraNotes']
    }
  }

  productMapping(res) {
    var productMappingArray = [];
    for (let r of res) {
      productMappingArray.push({
        productInfoId: r['productInformation']['productInfoId'],
        productIdentificationType: r['productIdentification'] ? r['productIdentification']['productIdentification'] : '',
        productIdentificationNumber: r['productIdentificationNumber']
      })
    }
    return productMappingArray;
  }

  lotMapping(res) {
    var lotMappingArray = [];
    for (let r of res) {
      lotMappingArray.push({
        lotNumber: r['lotNumber'],
        lotType: r['lotType'],
        productIdentificationType: r['productIdentification'] ? r['productIdentification']['productIdentification'] : '',
        productIdentificationNumber: r['productIdentificationNumber'],
        cardinalCatalogNumber: r['cardinalCatalogNumber']
      })
    }
    return lotMappingArray;
  }

  dcMapping(res) {
    var dcArray = [];
    for (let r of res) {
      dcArray.push({
        dcId: r['distributionCenter']['distributionCentreId']
      })
    }
    return dcArray;
  }

  downloadTableData(dataSource, fileName, len?) {
    if (dataSource.length) {
      const length = Object.keys(dataSource[0]).length;
      const workSheet = XLSX.utils.json_to_sheet(dataSource);
      const range = XLSX.utils.decode_range(workSheet['!ref']);
      for (let i = range.s.r; i <= range.e.c; ++i) {
        const name = XLSX.utils.encode_col(i) + '1';
        if (!workSheet[name]) continue;
        switch (workSheet[name].v) {
          case 'eventNumber': workSheet[name].v = "Event Number"; break;
          case 'sendDate': workSheet[name].v = "Sent Date"; break;
          case 'eventType': workSheet[name].v = "Event Type"; break;
          case 'vendorName': workSheet[name].v = "Vendor Name"; break;
          case 'eventClass': workSheet[name].v = "Event Class"; break;
          case 'status': workSheet[name].v = "Event Status"; break;
          case 'level': workSheet[name].v = "Event Level"; break;
          case 'progress': workSheet[name].v = "Progress Percentage"; break;
          default: workSheet[name].v = this.camelCaseToTitleCase(workSheet[name].v);
        }
      }
      workSheet['!cols'] = dataSource.map(() => ({ wch: 30 }))
      if (len) {
        for (let j = length - 1; j > length - len; --j) {
          workSheet['!cols'][j] = { hidden: true };
        }
      }
      const workBook: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workBook, workSheet, 'sheet1');
      XLSX.writeFile(workBook, fileName);
    }
    else {
      this.dialog.open(ErrorDialogComponent, {
        data: { message: 'Table is Empty! Nothing to export.', delete: false },
        panelClass: 'error-dialog'
      });
    }
  }
setUserRolePermission(data){
  this.userPermissionData = data;
}
getUserRolePermission(){
 return  this.userPermissionData;
}
  camelCaseToTitleCase(in_camelCaseString) {
    var result = in_camelCaseString
      .replace(/(_)+/g, ' ')
      .replace(/([a-z])([A-Z][a-z])/g, "$1 $2")
      .replace(/([A-Z][a-z])([A-Z])/g, "$1 $2")
      .replace(/([a-z])([A-Z]+[a-z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z][a-z])/g, "$1 $2")
      .replace(/([a-z]+)([A-Z0-9]+)/g, "$1 $2")

      // Note: the next regex includes a special case to exclude plurals of acronyms, e.g. "ABCs"
      .replace(/([A-Z]+)([A-Z][a-rt-z][a-z]*)/g, "$1 $2")
      .replace(/([0-9])([A-Z][a-z]+)/g, "$1 $2")

      // Note: the next two regexes use {2,} instead of + to add space on phrases like Room26A and 26ABCs but not on phrases like R2D2 and C3PO"
      .replace(/([A-Z]{2,})([0-9]{2,})/g, "$1 $2")
      .replace(/([0-9]{2,})([A-Z]{2,})/g, "$1 $2")
      .trim();

    // capitalize the first letter
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  getProductInfo(res){
    let selectedProducts = [];
    for (let r of res){
      let productInformationList = [];
      let productList = [];
      for (let p of r['productInformation']){
        productInformationList.push({
          items: {
            cardinalHealthCatalogNumber: p['cardinalHealthCatalogNumber'],
            vendorName: p['vendorName'],
            productDescription: p['productDescription'],
            dscsaFl: p['dscsaFl'],
            lots: p['lots'].toString().replaceAll(",", " "),
            lotType: p['lotType']
          }
        });
      }
      for (let product of productInformationList){
        productList.push(product['items']);
      }
      selectedProducts.push({
        items: productList,
        productIdentificationType: r['productIdentificationType'],
        productIdentificationNumber: r['productIdentificationNumber']
      });
    }
    return selectedProducts;
  }
}
